import { GlassButton, GlassButtonDropdown } from "./GlassButtons";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassInputControl, GlassInputGroup, GlassInputGroupEscape, GlassInputGroupTitle, GlassOption, GlassSelect } from "./GlassInput";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MapitStateContext } from "src/states/MapitState";
import { bbox, buffer } from "@turf/turf";
import { addPreviewLayer, removePreviewLayer } from 'src/utils/mapLayerHelpers';
import { DataCollector } from "src/dataCollector/DataCollector";
import { ProtectedFeature } from "./ProtectedFeature";
import { ApplicationStateContext, Feature, FeatureType } from "src/states/ApplicationState";
import { wfsEndpoints } from "src/dataCollector/WFSCollectorEndpoints";
import { message } from "./Message";
import { GeoJSONSource } from "maplibre-gl";
import { ConflictSearchParams } from "./ConflictSearchResults";
import { geomWithBuffer } from "./ConflictSearchCalculations";
import { AdvancedDragModal, ADModalBody, ADModalFooter } from "src/componentsUtils/AdvancedDragModal";
import { FeatureLayer } from "src/managers/WmsLayerFunc";
import * as mitSoft from "../mit-soft-settings.json";
import { GlassCheckbox } from "./MitGlassComponents";
import { LayerVariabilityType } from "src/common/managers/Types";
import { getAllGroupsAndDecendedLayers } from "src/managers/OldHierarchy";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

type SearchAreaOption = {
   value: string,
   label: string,
   type: "layer" | "group",
   layerIds: number[],
   feature: GeoJSON.FeatureCollection<GeoJSON.Geometry>
};

type ConflictSearchMode = "active" | "profile" | "single";

export function ConflictSearchDialog({ window }: { window: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: mapitState } = useContext(MapitStateContext);
   const [bufferDistance, setBufferDistance] = useState<number>(0);
   const [endpoint, setEndpoint] = useState<keyof typeof wfsEndpoints>("Matriklen2");
   const [whoAmI, setWhoAmI] = useState<string | null>(null);
   const [featureType, setFeatureType] = useState<string>(() => getFirstFeatureType("Matriklen2"));
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const accessibleProfiles = useMemo(() => profiles.filter(p => !p.feature || hasAccessToFeature(p.feature)), [hasAccessToFeature]);
   const [selectedProfile, setSelectedProfileId] = useState<ConflictProfile<WFSEndpointsType>>(() => accessibleProfiles[0]);

   const [searchFeature, setSearchFeature] = useState<{ layerIds: number[], feature?: GeoJSON.FeatureCollection<GeoJSON.Geometry> } | undefined>(undefined);

   const [selectedSearchArea, setSelectedSearchArea] = useState<string | undefined>(undefined);
   const [searchMode, setSearchMode] = useState<ConflictSearchMode>("active");
   const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);

   const effectiveSearchMode = searchMode === "single" ? 'datasource'
      : searchMode === "profile" ? 'profile'
         : 'activelayers';
   const effectiveIncludeDataLayers = searchMode === "active";
   const activeDataLayerIds = useMemo(() => Object.keys(mapitState.layers).filter((layerId) => mapitState.layers[layerId].visible).map((layerId) => Number(layerId)), [mapitState.layers]);
   const activeTargetDataLayerIds = useMemo(() => activeDataLayerIds.filter((layerId) => !searchFeature?.layerIds.includes(Number(layerId))), [activeDataLayerIds, searchFeature]);
   const hasActiveLayers = mapitState.selectedFeatureLayerKeys.length > 0 || activeTargetDataLayerIds.length > 0;
   const canSubmit = Boolean(searchFeature?.feature?.features?.length) && (searchMode !== "active" || hasActiveLayers);

   const searchAreaOptions = useMemo<SearchAreaOption[]>(() => {
      const selectableLayers = Object.values(mapitState.layers).filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer);
      const selectableLayerIds = new Set(selectableLayers.map(layer => layer.layerId));
      const layerOptions = selectableLayers.map((layer): SearchAreaOption => ({
         value: `layer:${layer.layerId}`,
         label: layer.datasetname,
         type: "layer",
         layerIds: [layer.layerId],
         feature: layer.geoJson ?? { type: "FeatureCollection", features: [] }
      }));
      const groupOptions = getAllGroupsAndDecendedLayers(mapitState.layerHierarchy)
         .map(group => ({ ...group, layers: group.layers.filter(layerId => selectableLayerIds.has(layerId)) }))
         .filter(group => group.layers.length > 0)
         .map((group): SearchAreaOption => ({
            value: `group:${group.id}`,
            label: group.name === "Top Level" ? Localization.getText("All Layers") : group.name,
            type: "group",
            layerIds: group.layers,
            feature: {
               type: "FeatureCollection",
               features: group.layers.flatMap(layerId => mapitState.layers[layerId]?.geoJson?.features ?? []).filter(Boolean)
            }
         }));
      return [...layerOptions, ...groupOptions];
   }, [mapitState.layerHierarchy, mapitState.layers]);

   useEffect(() => {
      const selectedOption = searchAreaOptions.find(option => option.value === selectedSearchArea) ?? searchAreaOptions[0];
      if (!selectedOption) {
         setSelectedSearchArea(undefined);
         setSearchFeature(undefined);
         return;
      }
      if (selectedOption.value !== selectedSearchArea) {
         setSelectedSearchArea(selectedOption.value);
      }
      setSearchFeature({ layerIds: selectedOption.layerIds, feature: selectedOption.feature });
   }, [searchAreaOptions, selectedSearchArea]);


   useEffect(() => {
      if (mapitState.map) {
         let map = mapitState.map.getMapPop();
         addPreviewLayer(map, "conflict-search-area");
         return () => {
            removePreviewLayer(map, "conflict-search-area");
         }
      }
   }, [mapitState.map]);

   useEffect(() => {
      if (mapitState.map && searchFeature?.feature?.features?.length) {
         let geom = searchFeature.feature;
         let map = mapitState.map.getMapPop();
         (map.getSource("conflict-search-area") as GeoJSONSource)?.setData(geomWithBuffer(geom, bufferDistance) as any);
      }
   }, [mapitState.map, searchFeature, bufferDistance]);

   useEffect(() => {
      if (mapitState.map) {
         let map = mapitState.map.getMapPop();
         map.moveLayer("conflict-search-area-fill")
         map.moveLayer("conflict-search-area-outline")
         map.moveLayer("conflict-search-area-linestring")
         map.moveLayer("conflict-search-area-point")
      }
   }, [mapitState.map, mapitState.layers]);

   async function handleClickSingleSearch() {
      handleClickProfileSearch({
         id: "single-search",
         name: "Single layer search",
         feature: Feature.ConflictSearch,
         layers: [{ source: endpoint, layer: featureType! }]
      });
   }

   async function handleClickProfileSearch(selectedProfile: Omit<ConflictSearchParams, "geom" | "buffer">) {
      if (!selectedProfile) {
         message.error(Localization.getText("Please select a profile"));
         return
      }
      if (!searchFeature?.feature?.features?.length) {
         message.error(Localization.getText("Please select a layer to search"));
         return;
      }
      let activeLayers = activeTargetDataLayerIds;
      if (!effectiveIncludeDataLayers) {
         activeLayers = [];
      }
      let searchParams: ConflictSearchParams = {
         ...selectedProfile,
         geom: searchFeature?.feature,
         buffer: bufferDistance,
         searchDataLayer: activeLayers
      }
      windowDispatch(openWindow(WindowId.ConflictSearchResults, { searchParams }));
   }

   return (
      <AdvancedDragModal
         PosDefault={{ left: "98px", top: "60px", width: "340px" }}
         title={Localization.getText("Conflict search")}
         onClose={() => windowDispatch(closeWindow(window.id))}
      >
         <ADModalBody>
            <GlassInputGroup style={{ gridTemplateColumns: "1fr", maxWidth: "unset", rowGap: "10px" }}>
               <GlassInputGroupEscape>
                  <GlassInputGroupTitle>{Localization.getText("Search area")}</GlassInputGroupTitle>
               </GlassInputGroupEscape>
               <GlassInputGroupEscape>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px", padding: "10px", border: "1px solid #dddd", borderRadius: "8px", background: "#fff6" }}>
                     <ConflictSearchField label={Localization.getText("Search layer")} info={Localization.getText("ConflictSearch:SearchLayerInfo")}>
                        <GlassInputControl>
                           <GlassSelect value={selectedSearchArea} onSelect={(value) => setSelectedSearchArea(value)} placeholder={Localization.getText("Select layer...")}>
                              {searchAreaOptions.map(option => (
                                 <GlassOption key={option.value} value={option.value} label={option.label} className="LayerOption">
                                    <span className="icon"><MIBIcon icon={option.type === "group" ? "layerGroup" : "layer"} /></span>
                                    <span className="label">{option.label}</span>
                                 </GlassOption>
                              ))}
                           </GlassSelect>
                        </GlassInputControl>
                     </ConflictSearchField>

                     <ConflictSearchField label={Localization.getText("Buffer")} info={Localization.getText("ConflictSearch:BufferInfo")}>
                        <GlassInputControl><input type="number" value={bufferDistance} onChange={(e) => setBufferDistance(Number(e.target.value))} /><span>m</span></GlassInputControl>
                     </ConflictSearchField>
                  </div>
               </GlassInputGroupEscape>

               <GlassInputGroupEscape>
                  <GlassInputGroupTitle>{Localization.getText("Check against")}</GlassInputGroupTitle>
               </GlassInputGroupEscape>
               <ConflictSearchRadio checked={searchMode === "active"} onClick={() => setSearchMode("active")} info={Localization.getText("ConflictSearch:AllActiveLayersInfo")}>{Localization.getText("All active layers")}</ConflictSearchRadio>
               <ConflictSearchRadio checked={searchMode === "profile"} onClick={() => setSearchMode("profile")} info={Localization.getText("ConflictSearch:LayerProfileInfo")}>{Localization.getText("Layer profile")}</ConflictSearchRadio>

               {searchMode === "profile" && (
                  <ConflictSearchField label={Localization.getText("Layer profile")} info={Localization.getText("ConflictSearch:LayerProfileSelectInfo")}>
                     <GlassInputControl>
                        <select value={selectedProfile.id} onChange={(e) => {
                           let profile = accessibleProfiles.find(p => p.id === e.target.value)
                           setSelectedProfileId(profile!);
                        }}>
                           {accessibleProfiles.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                        </select>
                     </GlassInputControl>
                  </ConflictSearchField>
               )}

               <GlassInputGroupEscape>
                  <button type="button" onClick={() => setAdvancedOpen(!advancedOpen)} style={{ width: "100%", display: "flex", alignItems: "center", gap: "8px", border: "none", background: "#fff8", borderRadius: "8px", padding: "8px 10px", cursor: "pointer", color: "#555" }}>
                     <MIBIcon icon={advancedOpen ? "expandUp" : "expandDown"} />
                     <span>{Localization.getText("ConflictSearch:Advanced")}</span>
                  </button>
               </GlassInputGroupEscape>

               {advancedOpen && (
                  <>
                     <ConflictSearchRadio checked={searchMode === "single"} onClick={() => setSearchMode("single")} info={Localization.getText("ConflictSearch:SingleLayerInfo")}>{Localization.getText("Single layer")}</ConflictSearchRadio>
                     {searchMode === "single" && (
                        <GlassInputGroupEscape>
                           <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px", padding: "10px", border: "1px solid #dddd", borderRadius: "8px", background: "#fff6" }}>
                              <ConflictSearchField label={Localization.getText("Register")} info={Localization.getText("ConflictSearch:RegisterInfo")}>
                                 <GlassInputControl>
                                    <select value={endpoint} onChange={(e) => {
                                       setEndpoint(e.target.value as keyof typeof wfsEndpoints)
                                       setFeatureType(getFirstFeatureType(e.target.value as keyof typeof wfsEndpoints))
                                    }}>
                                       {Object.keys(wfsEndpoints).filter((a) => wfsEndpoints[a].active !== false).map((key) => (
                                          <option key={key} value={key}>{key}</option>
                                       ))}
                                    </select>
                                 </GlassInputControl>
                              </ConflictSearchField>
                              {wfsEndpoints[endpoint]?.["whoAmI"] ?
                                 <ConflictSearchField label={Localization.getText("Email")} info={Localization.getText("ConflictSearch:EmailInfo")}>
                                    <GlassInputControl>
                                       <input type="email" value={whoAmI ?? ""} onChange={(e) => setWhoAmI(e.target.value)} />
                                    </GlassInputControl>
                                 </ConflictSearchField>
                                 : <></>
                              }

                              <ConflictSearchField label={Localization.getText("Layer")} info={Localization.getText("ConflictSearch:SingleLayerSelectInfo")}>
                                 <GlassInputControl>
                                    <select value={featureType} onChange={(e) => setFeatureType(e.target.value)}>
                                       {Object.keys(wfsEndpoints[endpoint]?.featureTypes ?? {}).map((key) => (
                                          <option key={key} value={key}>{key}</option>
                                       ))}
                                    </select>
                                 </GlassInputControl>
                              </ConflictSearchField>
                           </div>
                        </GlassInputGroupEscape>
                     )}
                  </>
               )}
            </GlassInputGroup>

         </ADModalBody>
         <ADModalFooter >

            {effectiveSearchMode === 'datasource' && SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ?
               <ProtectedFeature feature={Feature.Debugging} contentsIfNoAccess={<></>} >
                  <GlassButtonDropdown placement={"bottom-start"} onSelect={async (action) => {
                     switch (action) {
                        case "GetCapabilities":
                           console.log(await DataCollector.WFS.getCapabilities(endpoint))
                           break;
                        case "DescribeFeatureType":
                           console.log(await DataCollector.WFS.describeFeatureType(endpoint, featureType as never))
                           break;
                        case "GetFeatureBBOX":
                           if (searchFeature?.feature?.features?.length) {
                              let x = buffer(searchFeature.feature, bufferDistance || 0, { units: 'meters' });
                              if (x) {
                                 console.log(
                                    await DataCollector.WFS.getFeatureBBOX(endpoint, featureType as never, bbox(x).flat() as [number, number, number, number])
                                 );
                              }
                           }
                           break;
                     }
                  }} options={[
                     { text: "GetCapabilities", value: "GetCapabilities" },
                     { text: "DescribeFeatureType", value: "DescribeFeatureType" },
                     { text: "GetFeatureBBOX", value: "GetFeatureBBOX" },
                  ]} >
                     Debug
                  </GlassButtonDropdown>
               </ProtectedFeature>
               : <></>
            }
            <GlassButton disabled={!canSubmit} onClick={() => {
               if (searchMode === "active" && !hasActiveLayers) {
                  message.error(Localization.getText("Please select at least one active layer"));
               } else if (effectiveSearchMode === 'profile') {
                  if (!selectedProfile) message.error(Localization.getText("Please select a profile"));
                  handleClickProfileSearch(selectedProfile);
               } else if (effectiveSearchMode === 'datasource') {
                  handleClickSingleSearch()
               } else if (effectiveSearchMode === 'activelayers') {
                  let activeKeys = mapitState.selectedFeatureLayerKeys;
                  let allLayers = mapitState.featureLayers;
                  let layerProfile = createLayerFromActiveLayers(activeKeys, allLayers)
                  handleClickProfileSearch(layerProfile);
               } else if (effectiveSearchMode === 'datalayers') {
                  handleClickProfileSearch({
                     id: "datalayers-search",
                     name: "",
                     layers: []
                  })
               }
            }}>{Localization.getText("Find conflicts")}</GlassButton>

         </ADModalFooter>
      </AdvancedDragModal >
   );
}

function getFirstFeatureType(endpoint: keyof typeof wfsEndpoints): string {
   return Object.keys(wfsEndpoints[endpoint]?.featureTypes ?? {})[0] ?? "";
}

function ConflictSearchField({ label, info, children }: { label: string, info: string, children: React.ReactNode }) {
   return (
      <GlassInputGroupEscape>
         <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "6px", alignItems: "center" }}>
            <label style={{ color: "#555", fontSize: "13px" }}>{label}</label>
            <ConflictSearchInfoButton info={info} />
            <div style={{ gridColumn: "1 / -1" }}>{children}</div>
         </div>
      </GlassInputGroupEscape>
   )
}

function ConflictSearchRadio({ checked, onClick, info, children }: { checked: boolean, onClick: () => void, info: string, children: React.ReactNode }) {
   return (
      <GlassInputGroupEscape>
         <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "6px", alignItems: "center" }}>
            <GlassCheckbox checked={checked} className="radio-circle" onClick={onClick} style={{ minHeight: "42px", padding: "10px", border: "1px solid #dddd", borderRadius: "8px", background: "#fff8" }}>{children}</GlassCheckbox>
            <ConflictSearchInfoButton info={info} />
         </div>
      </GlassInputGroupEscape>
   )
}

function ConflictSearchInfoButton({ info }: { info: string }) {
   const [open, setOpen] = useState(false);
   const wrapperRef = useRef<HTMLSpanElement>(null);

   useEffect(() => {
      if (!open) return;

      function closeOnOutsideFocusOrClick(event: PointerEvent | FocusEvent) {
         const target = event.target;
         if (target instanceof Node && wrapperRef.current?.contains(target)) return;
         setOpen(false);
      }

      document.addEventListener("pointerdown", closeOnOutsideFocusOrClick);
      document.addEventListener("focusin", closeOnOutsideFocusOrClick);
      return () => {
         document.removeEventListener("pointerdown", closeOnOutsideFocusOrClick);
         document.removeEventListener("focusin", closeOnOutsideFocusOrClick);
      }
   }, [open]);

   return (
      <span ref={wrapperRef} style={{ position: "relative", display: "inline-flex", justifyContent: "flex-end" }}>
         <button type="button" aria-label={Localization.getText("Information")} onClick={() => setOpen(!open)} style={{ width: "26px", height: "26px", borderRadius: "50%", border: "1px solid #d0d0d0", background: "white", color: "var(--viamapGreen)", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontWeight: 700 }}>i</button>
         {open && (
            <div className="GlassButtonToolTip" style={{ position: "absolute", right: 0, top: "32px", zIndex: 1002, width: "min(260px, calc(100vw - 48px))", textAlign: "left", whiteSpace: "normal" }}>
               {info}
            </div>
         )}
      </span>
   )
}

function autoFindWMSLayer(endpoint: WFSEndpointsType): string {
   let layer = mitSoft.standardBackgroundLayers?.find((softLayer) => {
      if (softLayer.wfs) {
         return softLayer.wfs.key === endpoint.source && softLayer.wfs.type === endpoint.layer
      }
      return false;
   })
   if (!layer || typeof layer.layers !== "string") {
      console.error("Could not find WMS layer for endpoint", endpoint);
      return "";
   };
   let urlParams = layer?.urlParams as unknown as Record<string, string> || {};
   let params = new URLSearchParams({ ...urlParams, layers: layer.layers });
   return "" + layer.host + layer.path + "?" + params.toString();
}


// MARK: Profiles
export type WFSEndpointsType = { source: string, layer: string, title?: string, filter?: any, wmsSource?: string | ((a: WFSEndpointsType) => string) };
export type ConflictProfile<T> = {
   id: string; name: string; layers: T[]; feature?: FeatureType,
   pages?: Omit<ConflictProfile<T>, "feature" | "id" | "pages">[]
};
const profiles: ConflictProfile<WFSEndpointsType>[] = [
   { id: "wms_test", name: "Test Fredskov og §3", layers: [{ source: "Matriklen2", layer: "FredskovFlade_Gaeldende", title: "Fredskov", wmsSource: autoFindWMSLayer }, { source: "Miljø Portal", layer: "2013 Beskyttede naturtyper", title: "Beskyttede naturtyper (§ 3)", wmsSource: autoFindWMSLayer }], feature: Feature.Debugging },
   { id: "profile_test", name: "Test Profile", layers: [{ source: "Matriklen2", layer: "SamletFastEjendom_Gaeldende", title: "Samlet fast ejendom" }, { source: "Matriklen2", layer: "Matrikelskel_Gaeldende", title: "Matrikelskel" }, { source: "Matriklen2", layer: "Skelpunkt_Gaeldende", title: "Skelpunkt" }], feature: Feature.Debugging },
   { id: "profile_error", name: "Profile with error layer", layers: [{ source: "Matriklen2", layer: "XXSamletFastEjendom_Gaeldende" }, { source: "Matriklen2", layer: "Matrikelskel_Gaeldende" }, { source: "Matriklen2", layer: "XXSkelpunkt_Gaeldende" }], feature: Feature.Debugging },
   {
      id: "profile_CTO",
      name: "Bæredygtig Udvikling",
      feature: Feature.ConflictSearch,
      layers: [
         { source: "Vejdirektoratet (Statsvej)", layer: "Vejbyggelinje(Statsvej)", title: "Vejbyggelinje (Statsvej)", },
         { source: "Miljø Portal", layer: "2008 Kirkebyggelinjer", title: "Kirkebyggelinjer" },
         { source: "Miljø Portal", layer: "2005 Skovbyggelinjer", title: "Skovbyggelinjer" },
         { source: "Miljø Portal", layer: "2003 Søbeskyttelseslinjer", title: "Søbeskyttelseslinjer" },
         { source: "Miljø Portal", layer: "2004 Åbeskyttelseslinjer", title: "Åbeskyttelseslinjer" },
         { source: "Miljø Portal", layer: "2018 NATURA 2000 - Fuglebeskyttelse", title: "NATURA 2000 - Fuglebeskyttelse" },
         { source: "Miljø Portal", layer: "2019 NATURA 2000 - Habitatområder", title: "NATURA 2000 - Habitatområder" },
         { source: "DKJord", layer: "Jordforurening, V1 kortlagt", title: "Jordforurening, V1 kortlagt" },
         { source: "DKJord", layer: "Jordforurening, V2 kortlagt", title: "Jordforurening, V2 kortlagt" },
         { source: "Miljøgis", layer: "Fredskov", title: "Fredskov", wmsSource: autoFindWMSLayer },
         { source: "Matriklen2", layer: "KlitfredningFlade_Gaeldende", title: "KlitfredningFlade" },
         { source: "Matriklen2", layer: "StrandbeskyttelseFlade_Gaeldende", title: "Strandbeskyttelsesflade" },
         { source: "Miljø Portal", layer: "2013 Beskyttede naturtyper", title: "Beskyttede naturtyper", wmsSource: autoFindWMSLayer },
         { source: "Miljø Portal", layer: "2014 Beskyttede vandløb", title: "Beskyttede vandløb" },
         { source: "Miljø Portal", layer: "2002 Beskyttede sten- og jorddiger", title: "Beskyttede sten- og jorddiger" },
         { source: "Miljø Portal", layer: "2009 Fredede områder", title: "Fredede områder" },
         { source: "FVM (Føde, Land og Fisk)", layer: "GLM_Fortidsminder_2026", title: "Fredede fortidsminder" },
         { source: "Plandata.dk", layer: "theme-knz-kystnaerhedszone-polygon", title: "Kystnærhedszone" },
      ]
   },
   {
      "id": "profileVeje",
      "name": "Veje",
      feature: Feature.ConflictSearch,
      "layers": [
         { source: "Matriklen2", layer: "Jordstykke_Gaeldende", title: "Registreret Vej", filter: [">", ["get", "vejareal"], 0] },
      ]
   },
   {
      id: "profile_Landbrugsmæglere",
      name: "Landbrugsmæglere",
      feature: Feature.ConflictSearch,
      layers: [
         { source: "Matriklen2", layer: "SamletFastEjendom_Gaeldende", title: "Samlet fast ejendom" },
         { source: "Matriklen2", layer: "Matrikelskel_Gaeldende", title: "Matrikelskel" },
         { source: "Matriklen2", layer: "Skelpunkt_Gaeldende", title: "Skelpunkt" },
         { source: "Miljøgis", layer: "drikkevandsinteresser", title: "Drikkevandsinteresser" },
         { source: "Miljøgis", layer: "vp3e2022_Nitratfølsomme indvindingsområder", title: "Nitratfølsomme indvindingsområder" },
         { source: "Miljø Portal", layer: "2142 Kommunernes status for boringsnære beskyttelsesområder (BNBO)", title: "Kommunernes status for boringsnære beskyttelsesområder (BNBO)" },
         { source: "Miljøgis", layer: "indsatsomraader", title: "Indsatsområder" },
         { source: "Miljøgis", layer: "indvindingsoplande_udenfor_osd", title: "Indvindingsoplande udenfor OSD" },
         { source: "Miljøgis", layer: "indvindingsoplande_alle", title: "Indvindingsoplande, Alle" },
         { source: "Miljøgis", layer: "foelsommeindvindingsomraader", title: "Følsomme indvindingsområder" },
         { source: "Miljø Portal", layer: "2014 Beskyttede vandløb", title: "Beskyttede vandløb" },
         { source: "Miljø Portal", layer: "2018 NATURA 2000 - Fuglebeskyttelse", title: "NATURA 2000 - Fuglebeskyttelse" },
         { source: "Miljø Portal", layer: "2019 NATURA 2000 - Habitatområder", title: "NATURA 2000 - Habitatområder" },
         { source: "Miljøgis", layer: "NP2-2016 Ramsarområder", title: "Ramsarområder NP2" },
         { source: "Miljø Portal", layer: "2020 Ramsar områder", title: "Ramsar områder" },
         { source: "Miljøgis", layer: "np11fredede_omraader_dai", title: "Fredede områder NP11" },
         { source: "Miljø Portal", layer: "2009 Fredede områder", title: "Fredede områder" },
         { source: "Miljø Portal", layer: "2140 Bekendtgørelsesfredninger", title: "Bekendtgørelsesfredninger" },
         { source: "FVM (Føde, Land og Fisk)", layer: "GLM_Fortidsminder_2026", title: "GLM Fortidsminder" },
         { source: "Miljø Portal", layer: "2003 Søbeskyttelseslinjer", title: "Søbeskyttelseslinjer" },
         { source: "Miljø Portal", layer: "2004 Åbeskyttelseslinjer", title: "Åbeskyttelseslinjer" },
         { source: "Miljø Portal", layer: "2008 Kirkebyggelinjer", title: "Kirkebyggelinjer" },
         { source: "Miljø Portal", layer: "2005 Skovbyggelinjer", title: "Skovbyggelinjer" },
         { source: "Miljø Portal", layer: "2013 Beskyttede naturtyper", title: "Beskyttede naturtyper", wmsSource: autoFindWMSLayer },
         { source: "Miljø Portal", layer: "2002 Beskyttede sten- og jorddiger", title: "Beskyttede sten- og jorddiger" },
         { source: "Matriklen2", layer: "StrandbeskyttelseLinje_Gaeldende", title: "Strandbeskyttelse" },
         { source: "Matriklen2", layer: "KlitfredningLinje_Gaeldende", title: "Klitfredning" },
         { source: "Miljøgis", layer: "Fredskov", title: "Fredskov" },
         { source: "Miljøgis", layer: "Naturmæssig særlig værdifuld skov", title: "Naturmæssig særlig værdifuld skov" },
         { source: "Miljøgis", layer: "Naturmæssigt særlig værdifuld skov på offentlige arealer (§25-skov)", title: "Naturmæssigt særlig værdifuld skov på offentlige arealer (§25-skov)" },
         { source: "FVM (Føde, Land og Fisk)", layer: "Jordbundskort_2024", title: "Jordbundskort" },
         { source: "Miljø Portal", layer: "2055 Råstofområder", title: "Råstofområder" },
         { source: "DKJord", layer: "Jordforurening, V1 kortlagt", title: "Jordforurening, V1 kortlagt" },
         { source: "DKJord", layer: "Jordforurening, V2 kortlagt", title: "Jordforurening, V2 kortlagt" },
         { source: "DKJord", layer: "Jordforurening, nuanceret", title: "Jordforurening, nuanceret" },
         { source: "Miljø Portal", layer: "2097 Igangværende påbud JFL", title: "Igangværende påbud JFL" },
         { source: "Plandata.dk", layer: "theme_pdk_planlagttrafikanlaeg_vedtaget", title: "Planlagt trafikanlæg vedtaget" },
         { source: "Plandata.dk", layer: "theme_pdk_planlagttrafikanlaeg_forslag", title: "Planlagt trafikanlæg forslag" },
         { source: "Plandata.dk", layer: "theme_pdk_vaerdifuldtlandbrugsomraade_vedtaget", title: "Værdifuldt landbrugsområde vedtaget" },
         { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget", title: "Lokalplan vedtaget" },
         { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag", title: "Lokalplan forslag" },
         { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_vedtaget_v", title: "Kommuneplanramme vedtaget" },
         { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_forslag_v", title: "Kommuneplanramme forslag" },
         { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag_vindmoelle", title: "Lokalplan forslag Vindmølle" },
         { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget_vindmoelle", title: "Lokalplan vedtaget Vindmølle" },
      ]
   },
   {
      id: "profile_sgav",
      name: "SGAV - MARS",
      feature: Feature.ConflictSearch,
      layers: [
         { source: "MARS SGAV", layer: "Naturpotentialer" },
         { source: "MARS SGAV", layer: "Projekter - Samlet" },
         { source: "MARS SGAV", layer: "Projekter - Tilskudsordning - Skovrejsning" },
         { source: "MARS SGAV", layer: "Skitseprojekter - Samlet" }
      ]
   },
   {
      id: "Grundsvandsprofilen",
      name: "Grundvand-profilen",
      feature: Feature.ConflictSearch,
      layers: [
         { source: "Miljøgis", layer: "drikkevandsinteresser", title: "Drikkevandsinteresser" },
         { source: "Miljøgis", layer: "saarbaregrundvandsdannendeomraader", title: "Sårbare grundvandsdannende områder" },
         { source: "Miljøgis", layer: "bnbo", title: "Boringsnære beskyttelsesområder" },
         { source: "Miljøgis", layer: "foelsommeindvindingsomraader", title: "Følsomme indvindingsområder" },
         { source: "Miljøgis", layer: "indvindingsoplande_udenfor_osd", title: "Indvindingsoplande udenfor OSD" },
         { source: "Miljø Portal", layer: "2112 Kommunale indsatsplaner for grundvandsbeskyttelse", title: "Kommunale indsatsplaner for grundvandsbeskyttelse" },
      ]
   },
   {
      id: "profile_NyboligLandbrug",
      name: "Landbrugsmæglere (udvidet)",
      feature: Feature.ConflictSearch,
      layers: [],
      pages: [
         {
            name: "01 Matrikelkort og optaget vej", layers: [
               { source: "Matriklen2", layer: "Matrikelskel_Gaeldende", title: "Matrikelskel" },
               { source: "Matriklen2", layer: "OptagetVej_Gaeldende", title: "Optaget vej" }
            ]
         },
         {
            name: "02 Lokalplan",
            layers: [
               { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget", title: "Lokalplan vedtaget" },
               { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag", title: "Lokalplan forslag" },
               { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget_vindmoelle", title: "Lokalplan vedtaget Vindmølle" },
               { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag_vindmoelle", title: "Lokalplan forslag Vindmølle" },
            ]
         },
         {
            name: "03 Kommuneplan",
            layers: [
               { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_vedtaget_v", title: "Kommuneplanramme vedtaget" },
               { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_forslag_v", title: "Kommuneplanramme forslag" },
            ]
         },
         {
            name: "04 Bygge og beskyttelseslinjer", layers: [
               { source: "Miljø Portal", layer: "2008 Kirkebyggelinjer", title: "Kirkebyggelinjer" },
               { source: "Miljø Portal", layer: "2005 Skovbyggelinjer", title: "Skovbyggelinjer" },
               { source: "Miljø Portal", layer: "2003 Søbeskyttelseslinjer", title: "Søbeskyttelseslinjer" },
               { source: "Miljø Portal", layer: "2004 Åbeskyttelseslinjer", title: "Åbeskyttelseslinjer" },
               { source: "Plandata.dk", layer: "theme-knz-kystnaerhedszone-polygon", title: "Kystnærhedszone" },
            ]
         },
         {
            name: "05 Beskyttede diger og fortidsminder", layers: [
               { source: "Miljø Portal", layer: "2002 Beskyttede sten- og jorddiger", title: "Beskyttede sten- og jorddiger" },
               { source: "FVM (Føde, Land og Fisk)", layer: "GLM_Fortidsminder_2026", title: "GLM Fortidsminder" },

            ]
         },
         {
            name: "06 Fredede områder", layers: [
               { source: "Miljøgis", layer: "np11fredede_omraader_dai", title: "Fredede områder NP11" },
               { source: "Miljø Portal", layer: "2009 Fredede områder", title: "Fredede områder" },
               { source: "Miljø Portal", layer: "2140 Bekendtgørelsesfredninger", title: "Bekendtgørelsesfredninger" },
            ]
         },
         {
            name: "07 Kulturarv bygninger", layers: [
               { source: "Plandata.dk", layer: "theme_pdk_kulturhistoriskbevaringsvaerdi_vedtaget", title: "Kulturhistorisk bevaringsværdi vedtaget" }
            ]
         },
         {
            name: "08 Fredskov og klit-strandbeskyttelse", layers: [
               { source: "Matriklen2", layer: "StrandbeskyttelseLinje_Gaeldende", title: "Strandbeskyttelse" },
               { source: "Matriklen2", layer: "KlitfredningLinje_Gaeldende", title: "Klitfredning" },
               { source: "Miljøgis", layer: "Fredskov", title: "Fredskov" },
            ]
         },
         {
            name: "10 Natura 2000 og Ramsar områder", layers: [
               { source: "Miljø Portal", layer: "2018 NATURA 2000 - Fuglebeskyttelse", title: "NATURA 2000 - Fuglebeskyttelse" },
               { source: "Miljø Portal", layer: "2019 NATURA 2000 - Habitatområder", title: "NATURA 2000 - Habitatområder" },
               { source: "Miljøgis", layer: "NP2-2016 Ramsarområder", title: "Ramsarområder NP2" },
               { source: "Miljø Portal", layer: "2020 Ramsar områder", title: "Ramsar områder" },
               { source: "Miljøgis", layer: "Natur- og vildtreservater", title: "Natur- og vildtreservater" }
            ]
         },
         {
            name: "11 BNBO og grundvandsbeskyttelse", layers: [
               { source: "Miljøgis", layer: "vp3e2022_Nitratfølsomme indvindingsområder", title: "Nitratfølsomme indvindingsområder" },
               { source: "Miljø Portal", layer: "2142 Kommunernes status for boringsnære beskyttelsesområder (BNBO)", title: "Kommunernes status for boringsnære beskyttelsesområder (BNBO)" },
            ]
         },
         {
            name: "12 Retentionskort", layers: [
               { source: "Miljøgis", layer: "dk_retentionskort_2020", title: "Retentionskort" }
            ]
         },
         {
            name: "13 Jordforurening", layers: [
               { source: "DKJord", layer: "Jordforurening, V1 kortlagt", title: "Jordforurening, V1 kortlagt" },
               { source: "DKJord", layer: "Jordforurening, V2 kortlagt", title: "Jordforurening, V2 kortlagt" },
               { source: "DKJord", layer: "Jordforurening, nuanceret", title: "Jordforurening, nuanceret" },
               { source: "Miljø Portal", layer: "2097 Igangværende påbud JFL", title: "Igangværende påbud JFL" },
            ]
         },
         {
            name: "14 Drikkevand", layers: [
               { source: "Miljøgis", layer: "drikkevandsinteresser", title: "Drikkevandsinteresser" },
               { source: "Miljøgis", layer: "indvindingsoplande_alle", title: "Indvindingsoplande, Alle" },
               { source: "Miljøgis", layer: "foelsommeindvindingsomraader", title: "Følsomme indvindingsområder" },
               { source: "Miljøgis", layer: "indvindingsoplande_udenfor_osd", title: "Indvindingsoplande udenfor OSD" },
               { source: "Miljøgis", layer: "indsatsomraader", title: "Indsatsområder" },

            ]
         },
         {
            name: "15 Marker og markblokke 2026", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "Marker_2026", title: "Marker 2026" },
               { source: "FVM (Føde, Land og Fisk)", layer: "Markblokke_2026", title: "Markblokke 2026" },
            ]
         },
         {
            name: "16 Markinfo", layers: [

            ]
         },
         {
            name: "17 Jordbundskort 2024", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "Jordbundskort_2024", title: "Jordbundskort" },
            ]
         },
         {
            name: "18 Vandområde planer", layers: [

            ]
         },
         {
            name: "19 Skovrejsning", layers: [
               { source: "Plandata.dk", layer: "theme_pdk_skovrejsningsomraade_forslag", title: "Skovrejsningsområde forslag" },
               { source: "Plandata.dk", layer: "theme_pdk_skovrejsningsomraade_vedtaget", title: "Skovrejsningsområde vedtaget" },
               { source: "Miljøgis", layer: "Arealkort 2022 (digital skovkort)", title: "Arealkort 2022 (digital skovkort)" }
            ]
         },
         {
            name: "20 Skov HNV naturværdi", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "HNV_skov_2024", title: "HNV skov 2024" },
            ]
         },
         {
            name: "21 Arealanvendelse 2024 og markkrat 2025", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "Arealanvendelse_2024", title: "Arealanvendelse 2024" },
               { source: "FVM (Føde, Land og Fisk)", layer: "Markkrat_2026", title: "Markkrat 2026" }
            ]
         },
         {
            name: "26 Vandboringer", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "Vandboringer_i_IMK_2024", title: "Vandboringer i IMK 2024" }]
         }
      ]
   }
]


function createLayerFromActiveLayers(activeKeys: string[], allLayers: Record<string, FeatureLayer>): Omit<ConflictSearchParams, "geom" | "buffer"> {
   let layers = activeKeys.map(key => {
      let layer = allLayers[key].layer;
      if ("wfs" in layer && layer.wfs && layer.wfs["key"] && layer.wfs["type"]) {
         return {
            source: layer.wfs["key"], layer: layer.wfs["type"], title: key.split("\\").at(-1),
            filter: (allLayers[key] as any)?.mitValues?.userFilter ?? undefined
         }
      }
      return undefined
   }).filter(l => l !== undefined);

   return {
      id: "Active Layers", name: "Aktive lag", layers: layers, feature: Feature.BasicMap,
   }
}
